import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add, ContentCopy, FileUpload, LocalShipping } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import ImportMapitShipOrdersDialog from './ImportMapitShipOrdersDialog';
import CloneOrdersDialog from './CloneOrdersDialog';
import { ConfirmOrdersForShipmentDialog } from './index';

/**
 * MerchantActions - Action buttons displayed for merchant users
 */
function MerchantActions({
    status,
    selectedOrdersIds,
    selectedOrders,
    selectedReturnOrder,
    shipmentType,
    isValidatingWarehouses,
    formattingLoading,
    isDownloadingAwb,
    openImportDialog,
    openCreateShipmentDialog,
    onOpenOrderDialog,
    onOpenImportDialog,
    onCloseImportDialog,
    onImportSuccess,
    onCreateShipment,
    onBulkAssign,
    onDownloadAwb,
    onCloseShipmentDialog,
    openCloneDialog,
    onOpenCloneDialog,
    onCloseCloneDialog,
    onCloneSuccess,
}) {
    const { t } = useTranslation();
    const theme = useTheme();

    const showAwbButton = !['ORDER_PENDING', 'ALL_ORDERS', 'ORDER_CANCELED', 'ORDER_COMPLETED'].includes(status);

    return (
        <>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Button
                    id="create-order-btn"
                    startIcon={<Add />}
                    sx={{ textTransform: 'none' }}
                    variant="contained"
                    color="primary"
                    onClick={onOpenOrderDialog}
                >
                    {t('Create order')}
                </Button>
                <Button
                    startIcon={<FileUpload />}
                    sx={{ textTransform: 'none' }}
                    variant="contained"
                    color="primary"
                    onClick={onOpenImportDialog}
                >
                    {t('Import orders')}
                </Button>
                <ImportMapitShipOrdersDialog
                    open={openImportDialog}
                    onClose={onCloseImportDialog}
                    onSuccess={onImportSuccess}
                />
                {!['ALL_ORDERS'].includes(status) && (
                    <>
                        <Button
                            startIcon={<ContentCopy />}
                            sx={{ textTransform: 'none' }}
                            variant="contained"
                            color="primary"
                            disabled={!selectedOrdersIds.length}
                            onClick={onOpenCloneDialog}
                        >
                            {t('Clone Orders')}
                        </Button>
                        <CloneOrdersDialog
                            open={openCloneDialog}
                            onClose={onCloseCloneDialog}
                            selectedOrdersIds={selectedOrdersIds}
                            onSuccess={onCloneSuccess}
                        />
                    </>
                )}
                {/* Select Shipping Company Button */}
                {status === 'ORDER_PENDING' && (
                    <LoadingButton
                        id="choses-company-order"
                        loading={isValidatingWarehouses || formattingLoading}
                        loadingPosition="start"
                        startIcon={<LocalShipping />}
                        sx={{ textTransform: 'none' }}
                        variant="contained"
                        color="primary"
                        disabled={
                            !selectedOrdersIds.length ||
                            isValidatingWarehouses ||
                            formattingLoading
                        }
                        onClick={onCreateShipment}
                    >
                        {t('Select Shipping Company')}
                    </LoadingButton>
                )}
                {/* Bulk: one company for every selected order */}
                {/* {status === 'ORDER_PENDING' && (
                    <LoadingButton
                        id="bulk-assign-company-order"
                        loading={isValidatingWarehouses || formattingLoading}
                        loadingPosition="start"
                        startIcon={<LocalShipping />}
                        sx={{ textTransform: 'none' }}
                        variant="contained"
                        color="secondary"
                        disabled={
                            !selectedOrdersIds.length ||
                            isValidatingWarehouses ||
                            formattingLoading
                        }
                        onClick={onBulkAssign}
                    >
                        {t('bulkAssign.assignAllButton', { total: selectedOrdersIds.length })}
                    </LoadingButton>
                )} */}
                {showAwbButton && (
                    <>
                        <LoadingButton
                            loading={isDownloadingAwb}
                            startIcon={<LocalShipping />}
                            sx={{ textTransform: 'none' }}
                            variant="contained"
                            color="primary"
                            disabled={!selectedOrdersIds.length}
                            onClick={onDownloadAwb}
                        >
                            {t('DownLoad AWB')}
                        </LoadingButton>
                        <Typography
                            variant="caption"
                            color="secondary.main"
                            maxWidth={theme.direction === 'rtl' ? 230 : 330}
                        >
                            {t('awbHint')}
                        </Typography>
                    </>
                )}
            </Box>
        </>
    );
}

MerchantActions.propTypes = {
    status: PropTypes.string.isRequired,
    selectedOrdersIds: PropTypes.array.isRequired,
    selectedOrders: PropTypes.array.isRequired,
    selectedReturnOrder: PropTypes.array,
    shipmentType: PropTypes.string,
    isValidatingWarehouses: PropTypes.bool,
    formattingLoading: PropTypes.bool,
    isDownloadingAwb: PropTypes.bool,
    openImportDialog: PropTypes.bool,
    openCreateShipmentDialog: PropTypes.bool,
    onOpenOrderDialog: PropTypes.func.isRequired,
    onOpenImportDialog: PropTypes.func.isRequired,
    onCloseImportDialog: PropTypes.func.isRequired,
    onImportSuccess: PropTypes.func.isRequired,
    onCreateShipment: PropTypes.func.isRequired,
    onBulkAssign: PropTypes.func,
    onDownloadAwb: PropTypes.func.isRequired,
    onCloseShipmentDialog: PropTypes.func.isRequired,
    openCloneDialog: PropTypes.bool,
    onOpenCloneDialog: PropTypes.func.isRequired,
    onCloseCloneDialog: PropTypes.func.isRequired,
    onCloneSuccess: PropTypes.func,
};

MerchantActions.defaultProps = {
    selectedReturnOrder: [],
    shipmentType: null,
    isValidatingWarehouses: false,
    formattingLoading: false,
    isDownloadingAwb: false,
    openImportDialog: false,
    openCreateShipmentDialog: false,
    openCloneDialog: false,
    onCloneSuccess: null,
    onBulkAssign: null,
};

export default memo(MerchantActions);
